/**
 * システムアイコン一覧
 *
 * 選択ボタンのスタイル：
 * - hover: 不使用（スマホ向け）
 * - transition: 不使用
 * - btnDelay: 不使用（選択ボタンでは不要）
 */

import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import { userIconPrefixes } from "app/assets";
import I18n from "common/i18n";
import Constants from "common/constant";
import { usePlaySound } from "common/utils";
import { CheckMark } from "features/user/assets";
import { PresetIconCategory } from "../types";

interface PresetIconGridProps {
  /** アイコン選択時のコールバック */
  onIconSelect: (url: string) => void;
  /** 現在選択中のアイコンURL */
  selectedIcon?: string | null;
}

const CATEGORY_OPTIONS = [
  {
    key: PresetIconCategory.ALL,
    labelKey: "MSG_ICON_CATEGORY_ALL",
  },
  {
    key: PresetIconCategory.BOY,
    labelKey: "MSG_ICON_CATEGORY_BOY",
  },
  {
    key: PresetIconCategory.GIRL,
    labelKey: "MSG_ICON_CATEGORY_GIRL",
  },
];

export const PresetIconGrid = ({ onIconSelect, selectedIcon }: PresetIconGridProps) => {
  const play = usePlaySound();
  const [category, setCategory] = useState(PresetIconCategory.ALL);
  const defaultAssets = useSelector((state: RootState) => state.config.assets);

  const handleIconClick = (url: string) => {
    play();
    onIconSelect(url);
  };

  const handleCategoryChange = (newCategory: PresetIconCategory) => {
    play();
    setCategory(newCategory);
  };

  const systemIcons = userIconPrefixes.reduce<Array<{ url: string; key: string }>>(
    (acc, prefix) => [
      ...acc,
      ...defaultAssets[prefix]
        .filter((asset) => asset.fileSize > 0)
        .filter((asset) =>
          category === PresetIconCategory.ALL ? true : asset.category === category,
        )
        .map((asset) => ({
          url: `${Constants.assetHost}/${asset.fileKey}`,
          key: asset.fileKey,
        })),
    ],
    [],
  );

  return (
    <div className="flex-col-el h-full min-h-0">
      {/* カテゴリタブ */}
      <div className="flex-row-center h-[74px] mb-4 bg-gray2-10 rounded-[12px] border-[4px] border-solid border-white">
        {CATEGORY_OPTIONS.map((option, index) => {
          const isActive = category === option.key;
          const isFirst = index === 0;
          const isLast = index === CATEGORY_OPTIONS.length - 1;
          return (
            <div
              key={option.key}
              onClick={() => handleCategoryChange(option.key)}
              className={`flex-col-center cursor-pointer flex-1 h-full ${
                isActive ? "bg-green-alt" : ""
              } ${isFirst ? "rounded-l-[8px]" : ""} ${isLast ? "rounded-r-[8px]" : ""}`}
            >
              <p className="flex-col-center text text-textcolor-black !text-[20px] !leading-[22px]">
                {I18n.t(option.labelKey)}
              </p>
            </div>
          );
        })}
      </div>

      {/* アイコングリッド */}
      <div className="flex-col-el flex-1 overflow-y-auto p-4">
        <div className="grid grid-cols-7 gap-4">
          {systemIcons.map((icon) => (
            <div
              key={icon.key}
              onClick={() => handleIconClick(icon.url)}
              className="flex-col-center w-[90px] h-[90px] cursor-pointer relative"
            >
              {selectedIcon === icon.url && (
                <div className="flex-col-el absolute top-[-16px] left-[-16px] z-50">
                  <CheckMark />
                </div>
              )}
              <img
                src={icon.url}
                alt=""
                className={`flex-col-center w-full h-full object-contain border-solid border-6 ${
                  selectedIcon === icon.url ? "border-green-100/40" : "border-gray-80/40"
                }`}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
